(() => {
  const SELECTOR = '[data-custom-product-slider]';

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function initSlider(root) {
    const viewport = root.querySelector('[data-custom-product-slider-viewport]');
    const slides = Array.from(root.querySelectorAll('[data-custom-product-slider-slide]'));
    if (!viewport || slides.length === 0) return;

    const axis = root.dataset.axis === 'y' ? 'y' : 'x';
    const count = slides.length;

    root.style.setProperty('--cps-seg', String(1 / count));

    let activeIndex = 0;
    const getPos = (e) => (axis === 'y' ? e.clientY : e.clientX);
    const getScroll = () => (axis === 'y' ? viewport.scrollTop : viewport.scrollLeft);
    const setScroll = (v) => {
      const max = getMaxScroll();
      const next = clamp(v, 0, max);
      if (axis === 'y') viewport.scrollTop = next;
      else viewport.scrollLeft = next;
    };
    const getMaxScroll = () => 
      axis === 'y' 
        ? Math.max(0, viewport.scrollHeight - viewport.clientHeight) 
        : Math.max(0, viewport.scrollWidth - viewport.clientWidth);

    const setActive = (idx) => {
      activeIndex = clamp(idx, 0, count - 1);
      root.style.setProperty('--cps-index', String(activeIndex / count));
    };

    const updateIndicator = () => {
      const max = getMaxScroll() || 1;
      const current = getScroll();
      const progress = current / max;
      const seg = 1 / count;
      const maxOffset = Math.max(0, 1 - seg);

      root.style.setProperty('--cps-offset', String(clamp(progress, 0, 1) * maxOffset));
    };

    let scrollRaf = 0;
    const onScroll = () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        updateIndicator();
      });
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    const io = new IntersectionObserver(
      (entries) => {
        let best = { idx: activeIndex, ratio: 0 };
        for (const e of entries) {
          const idx = Number(e.target.dataset.index || 0);
          const ratio = e.intersectionRatio || 0;
          if (ratio > best.ratio) best = { idx, ratio };
        }
        if (best.ratio > 0.35) setActive(best.idx);
      },
      { root: viewport, threshold: [0.3, 0.5, 0.7, 0.9] }
    );

    slides.forEach((s) => io.observe(s));

    if (axis === 'y') {
      const WHEEL_GAIN = 2.0;
      const TRACKPAD_GAIN = 2.4;
      const WHEEL_SMOOTH = 0.22;

      let wheelTarget = null;
      let wheelRaf = 0;
      const stopWheel = () => {
        if (wheelRaf) cancelAnimationFrame(wheelRaf);
        wheelRaf = 0;
        wheelTarget = null;
      };
      const tickWheel = () => {
        wheelRaf = 0;
        if (wheelTarget == null) return;
        wheelTarget = clamp(wheelTarget, 0, getMaxScroll());
        const current = getScroll();
        const next = current + (wheelTarget - current) * WHEEL_SMOOTH;
        setScroll(next);
        if (Math.abs(wheelTarget - next) > 0.5) {
          wheelRaf = requestAnimationFrame(tickWheel);
        } else {
          setScroll(wheelTarget);
          wheelTarget = null;
        }
      };

      viewport.addEventListener('wheel', (e) => {
        if (viewport.scrollHeight <= viewport.clientHeight + 2) return;
        const looksLikeTrackpad = e.deltaMode === 0 && Math.abs(e.deltaY) < 40;
        e.preventDefault();
        let dy = e.deltaY;
        if (e.deltaMode === 1) dy *= 16;
        else if (e.deltaMode === 2) dy *= viewport.clientHeight;

        const gain = looksLikeTrackpad ? TRACKPAD_GAIN : WHEEL_GAIN;
        const delta = dy * gain;

        if (looksLikeTrackpad) {
          setScroll(getScroll() + delta);
          return;
        }

        wheelTarget = clamp((wheelTarget == null ? getScroll() : wheelTarget) + delta, 0, getMaxScroll());
        if (!wheelRaf) wheelRaf = requestAnimationFrame(tickWheel);
      }, { passive: false });
    }

    let isDown = false;
    let startPos = 0;
    let startScroll = 0;
    let velocity = 0;
    let lastPos = 0;
    let lastT = 0;
    let inertiaRaf = 0;

    const DRAG_GAIN = 3;
    const INERTIA_GAIN = 42;
    const FRICTION = 0.94;
    const STOP_EPS = 0.015;
    const DRAG_DIR = -1;

    const applyInertia = () => {
      if (Math.abs(velocity) > STOP_EPS) {
        setScroll(getScroll() + velocity * INERTIA_GAIN * DRAG_DIR);
        velocity *= FRICTION;
        inertiaRaf = requestAnimationFrame(applyInertia);
      } else {
        cancelAnimationFrame(inertiaRaf);
      }
    };

    viewport.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;

      isDown = true;
      root.classList.add('is-dragging');
      cancelAnimationFrame(inertiaRaf);
      if (axis === 'y') stopWheel();

      startPos = getPos(e);
      lastPos = startPos;
      lastT = performance.now();
      startScroll = getScroll();
      velocity = 0;

      viewport.style.scrollBehavior = 'auto';
      e.preventDefault();
    });

    window.addEventListener('mousemove', (e) => {
      if (!isDown) return;

      const currentPos = getPos(e);
      const dPos = currentPos - lastPos;
      
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      velocity = dPos / dt;
      lastPos = currentPos;
      lastT = now;

      setScroll(getScroll() + dPos * DRAG_GAIN * DRAG_DIR);
    });

    window.addEventListener('mouseup', () => {
      if (!isDown) return;

      isDown = false;
      root.classList.remove('is-dragging');
      
      viewport.style.scrollBehavior = '';
      
      inertiaRaf = requestAnimationFrame(applyInertia);
    });

    viewport.addEventListener('dragstart', (e) => e.preventDefault());
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.__customProductSliderInit) return;
      root.__customProductSliderInit = true;
      initSlider(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();